'use client';

import toast from 'react-hot-toast';
import {
  fireToastFromMessage,
  type FireToastFromMessageOptions,
} from './toast-from-message';
import type {
  PlatformMessagePosition,
  PlatformMessageRenderStyle,
} from './api';
import { getCurrentTenantSlug } from './platform-domain';

/**
 * Disparador unificado de toasts baseado em PlatformMessage (categoria toast).
 *
 * O catalogo de toasts vive no banco e e configurado pelo super-admin em
 * /admin/messages: cada chave tem texto + renderStyle + position + durationMs.
 * Este helper busca o catalogo uma unica vez (cache em memoria por
 * sessao) e dispara o toast no estilo configurado.
 *
 * Funciona em qualquer pagina (com ou sem QueryClientProvider) porque
 * usa fetch direto e cache modulo-level. Importante para /login,
 * /register, /forgot-password que nao tem o provider.
 *
 * Uso tipico:
 *
 *   try {
 *     await authApi.login(creds);
 *     void firePlatformToast('toast.success.login');
 *   } catch (err) {
 *     void firePlatformToast('toast.error.admin-access-denied', {
 *       fallback: 'Sem acesso',
 *     });
 *   }
 *
 * Quando a chave nao existe no catalogo (ainda nao seedada, ou erro de
 * rede), usa o fallback como texto e padrao global de estilo/duracao.
 */
export interface PlatformToastRow {
  key: string;
  value: string;
  description: string | null;
  renderStyle: PlatformMessageRenderStyle | null;
  position: PlatformMessagePosition | null;
  durationMs: number | null;
}

interface ToastCatalogResponse {
  items: PlatformToastRow[];
}

const CATALOG_URL = '/api/v1/platform/messages/toast-catalog';
const CACHE_TTL_MS = 5 * 60 * 1000;

// Cache por slug de tenant. Chave 'global' = sem tenant (host canonico
// da plataforma, ex.: 7barber.app/login). Cada tenant tem seu proprio cache para
// nao misturar overrides entre barbearias.
interface CacheEntry {
  catalog: Map<string, PlatformToastRow>;
  fetchedAt: number;
}

const cacheByScope = new Map<string, CacheEntry>();
const inflightByScope = new Map<string, Promise<Map<string, PlatformToastRow>>>();

function scopeKey(tenantSlug: string | null): string {
  return tenantSlug ?? 'global';
}

async function loadCatalog(
  tenantSlug: string | null,
): Promise<Map<string, PlatformToastRow>> {
  const key = scopeKey(tenantSlug);
  const now = Date.now();
  const cached = cacheByScope.get(key);
  if (cached && now - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.catalog;
  }
  const inflight = inflightByScope.get(key);
  if (inflight) return inflight;
  const promise = (async () => {
    try {
      const url = tenantSlug
        ? `${CATALOG_URL}?tenantSlug=${encodeURIComponent(tenantSlug)}`
        : CATALOG_URL;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) {
        throw new Error(`http ${res.status}`);
      }
      const data: ToastCatalogResponse = await res.json();
      const map = new Map<string, PlatformToastRow>();
      for (const item of data.items ?? []) {
        map.set(item.key, item);
      }
      cacheByScope.set(key, { catalog: map, fetchedAt: Date.now() });
      return map;
    } finally {
      inflightByScope.delete(key);
    }
  })();
  inflightByScope.set(key, promise);
  return promise;
}

export interface FirePlatformToastOptions extends FireToastFromMessageOptions {
  /**
   * Texto a usar caso a chave nao exista no catalogo (ainda nao seedada,
   * erro de rede, etc). Quando ausente, usa a propria chave como texto
   * (visivel apenas em modo dev).
   */
  fallback?: string;
  /**
   * Texto que sobrescreve o `value` do catalogo. Util para casos onde o
   * backend devolve a mensagem real (ex: validacao com placeholders ja
   * interpolados) e o frontend so quer aplicar o estilo configurado.
   */
  textOverride?: string;
}

/**
 * Dispara um toast respeitando renderStyle/position/durationMs salvos no
 * banco para a key. Se o catalogo ainda nao foi buscado, faz fetch antes
 * (toast aparece com pequeno atraso na primeira chamada da sessao).
 */
export async function firePlatformToast(
  key: string,
  options: FirePlatformToastOptions = {},
): Promise<void> {
  const tenantSlug = getCurrentTenantSlug();
  try {
    const catalog = await loadCatalog(tenantSlug);
    const row = catalog.get(key);
    if (row) {
      const merged: PlatformToastRow = {
        ...row,
        value: options.textOverride ?? row.value,
      };
      fireToastFromMessage(merged, {
        vars: options.vars,
        onAction: options.onAction,
      });
      return;
    }
  } catch {
    // ignora - cai no fallback abaixo
  }
  // Fallback - catalogo indisponivel ou key nao seedada
  const text = options.textOverride ?? options.fallback ?? key;
  const tone = inferTone(key);
  if (tone === 'success') {
    toast.success(text);
  } else if (tone === 'error') {
    toast.error(text);
  } else {
    toast(text);
  }
}

function inferTone(key: string): 'success' | 'error' | 'info' {
  if (key.includes('.success')) return 'success';
  if (key.includes('.error')) return 'error';
  return 'info';
}

/**
 * Invalida o cache em memoria. Util quando o operador acaba de editar
 * uma mensagem em /admin/messages e quer ver o efeito imediato no
 * proximo dispatch sem aguardar 5 min de TTL.
 */
export function invalidatePlatformToastCatalog(): void {
  cacheByScope.clear();
  inflightByScope.clear();
}
